import type { ComponentProps, ReactNode } from "react";
import { useId } from "react";
import { tv } from "tailwind-variants";

import type { FormErrorType } from "./";
import { focusField } from "./interaction";
import { ErrorMessage, IconWrapper, Label } from "./";

const inputVariants = tv({
  slots: {
    fieldContainer: "relative",
    label: "mb-2",
    inputContainer: "flex flex-row items-center rounded-full",
    leftIcon: "pointer-events-none absolute pl-3 text-content-subtle",
    rightIcon: "absolute right-3",
    input: [
      "block h-12 w-full rounded-full border border-line-strong bg-surface px-4 text-base text-content",
      "placeholder:text-content-subtle",
      "transition-[border-color,box-shadow] duration-150 hover:border-focus/60",
      focusField,
    ],
  },
  variants: {
    disabled: {
      true: {
        inputContainer: "opacity-50",
        input: "cursor-not-allowed",
      },
    },
    error: {
      true: {
        input:
          "border-danger text-content placeholder:text-danger/70 hover:border-danger focus:border-danger focus:ring-danger/25",
      },
    },
    left: {
      true: {
        input: "pl-10",
      },
    },
  },
});

const {
  fieldContainer,
  input,
  inputContainer,
  label: labelVariant,
  leftIcon,
  rightIcon,
} = inputVariants();

export type InputProps = {
  label?: ReactNode;
  compact?: boolean;
  error?: FormErrorType;
  left?: ReactNode;
  right?: ReactNode;
  rightWidth?: number;
  containerClassName?: string;
  labelClassName?: string;
  iconClassName?: string;
  errorClassName?: string;
} & ComponentProps<"input">;

export const Input = ({
  className,
  compact,
  containerClassName,
  error,
  errorClassName,
  iconClassName,
  id,
  label,
  labelClassName,
  left,
  right,
  rightWidth = 36,
  style,
  ...rest
}: InputProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hasError = Boolean(error);
  const describedBy = hasError && !compact ? errorId : undefined;

  return (
    <div className={fieldContainer({ className: containerClassName })} style={style}>
      {label ? (
        <Label
          className={labelVariant({ className: labelClassName })}
          htmlFor={inputId}
          label={label}
        />
      ) : null}

      <div className={inputContainer({ disabled: rest.disabled })}>
        {left ? (
          <div className={leftIcon({ className: iconClassName })}>
            <IconWrapper size="sm">{left}</IconWrapper>
          </div>
        ) : null}

        <input
          id={inputId}
          type="text"
          {...rest}
          aria-describedby={describedBy}
          aria-invalid={hasError || undefined}
          className={input({ className, error: hasError, left: Boolean(left) })}
          style={{ paddingRight: right ? rightWidth : undefined }}
        />

        {right ? (
          <IconWrapper
            className={rightIcon({ className: iconClassName })}
            style={{ width: rightWidth }}
          >
            {right}
          </IconWrapper>
        ) : null}
      </div>

      {!compact && <ErrorMessage className={errorClassName} error={error} id={errorId} />}
    </div>
  );
};
